import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, X } from "lucide-react";

// ============ WOLVOX GROUP REPORT (mapping manager) ============
export default function WolvoxGroupReportDialog({ open, onOpenChange, categories, onApplied }) {
  const [report, setReport] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [newTarget, setNewTarget] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/admin/wolvox/group-report"),
        api.get("/admin/wolvox/category-map"),
      ]);
      setReport(r1.data || []);
      setMappings(r2.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) refresh(); }, [open]);

  const mapGroup = async (wg, target) => {
    try {
      const { data } = await api.post("/admin/wolvox/category-map", {
        wolvox_group: wg, target_slug: target,
      });
      toast.success(`✓ ${data.updated_products} ürün yeni kategoriye taşındı`);
      setEditingGroup(null);
      setNewTarget("");
      await refresh();
      onApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eşleme kaydedilemedi");
    }
  };

  const removeMapping = async (wg) => {
    if (!window.confirm(`"${wg}" eşlemesi silinsin mi? (Bir sonraki sync'te eski haline döner)`)) return;
    try {
      await api.delete(`/admin/wolvox/category-map/${encodeURIComponent(wg)}`);
      toast.success("Eşleme kaldırıldı");
      await refresh();
    } catch {
      toast.error("Silinemedi");
    }
  };

  const grouped = React.useMemo(() => {
    const m = new Map();
    for (const r of report) {
      const key = r.wolvox_group || "(boş / sınıfsız)";
      if (!m.has(key)) {
        m.set(key, {
          wolvox_group: r.wolvox_group,
          total_products: 0,
          locked_products: 0,
          mapped_to: r.mapped_to,
          slugs: new Set(),
        });
      }
      const b = m.get(key);
      b.total_products += r.product_count;
      b.locked_products += r.locked_count;
      b.slugs.add(r.category_slug);
    }
    return Array.from(m.values()).sort((a, b) => b.total_products - a.total_products);
  }, [report]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="wolvox-report-dialog" className="bg-[#FAF7F2] border-[#E8E2D5] max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            📊 Wolvox Grup Raporu
          </DialogTitle>
          <DialogDescription>
            Akınsoft'tan gelen her <b>STOK.GRUBU</b> değeri hangi site kategorisine düşüyor?
            Yanlış eşleşenleri doğru kategoriye bağlayın — bir sonraki sync'te otomatik düzelir.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-[#E5A024]/10 border border-[#E5A024]/40 rounded-xl p-3 text-xs text-[#B4711C]">
          <b>⚠️ "(boş / sınıfsız)"</b> satırı → Wolvox'ta grubu olmayan veya ilk sync öncesi eklenen ürünler. Normal.
          <br/><b>🔗 Eşlemeler</b>: {mappings.length} tanımlı mapping — toplamda {grouped.length} farklı Wolvox grubu var.
        </div>

        {loading ? (
          <div className="text-center py-6 text-[#1B2A22]/60">Yükleniyor...</div>
        ) : (
          <div className="bg-white border border-[#E8E2D5] rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Wolvox Grubu (STOK.GRUBU)</TableHead>
                  <TableHead>Ürün Sayısı</TableHead>
                  <TableHead>Düştüğü Site Kategorisi</TableHead>
                  <TableHead>Eşleme (Mapping)</TableHead>
                  <TableHead>Aksiyon</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {grouped.map((g, idx) => (
                  <TableRow key={idx} data-testid={`wgroup-row-${idx}`}>
                    <TableCell>
                      <span className="font-mono font-bold text-[#2E5A36]">
                        {g.wolvox_group || "(boş / sınıfsız)"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono">{g.total_products}</span>
                      {g.locked_products > 0 && (
                        <span className="ml-2 text-[10px] text-[#D9653B]" title="Admin kilitli">
                          🔒{g.locked_products}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {Array.from(g.slugs).map((s) => (
                        <span key={s} className="inline-block bg-[#2E5A36]/10 text-[#2E5A36] rounded px-2 py-0.5 mr-1 mb-1 font-mono">
                          {s || "(hiç)"}
                        </span>
                      ))}
                    </TableCell>
                    <TableCell className="text-xs">
                      {g.mapped_to ? (
                        <span className="inline-flex items-center gap-1 bg-[#E5A024]/15 text-[#B4711C] rounded-full px-2 py-0.5 font-bold">
                          🔗 → {g.mapped_to}
                        </span>
                      ) : (
                        <span className="text-[#1B2A22]/40">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {editingGroup === g.wolvox_group ? (
                        <div className="flex items-center gap-1">
                          <Select value={newTarget} onValueChange={setNewTarget}>
                            <SelectTrigger data-testid={`wgroup-select-${idx}`} className="h-8 w-40 bg-white text-xs">
                              <SelectValue placeholder="Hedef..." />
                            </SelectTrigger>
                            <SelectContent>
                              {categories.map((c) => (
                                <SelectItem key={c.slug} value={c.slug}>
                                  {c.icon || "📦"} {c.name_tr}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            data-testid={`wgroup-save-${idx}`}
                            size="sm"
                            onClick={() => mapGroup(g.wolvox_group, newTarget)}
                            disabled={!newTarget}
                            className="h-8 px-2 bg-[#2E5A36] hover:bg-[#1B2A22] text-white rounded-full"
                          >
                            ✓
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setEditingGroup(null); setNewTarget(""); }}
                            className="h-8 px-2 rounded-full"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex gap-1">
                          {g.wolvox_group && (
                            <Button
                              data-testid={`wgroup-edit-${idx}`}
                              size="sm"
                              variant="outline"
                              onClick={() => { setEditingGroup(g.wolvox_group); setNewTarget(g.mapped_to || ""); }}
                              className="h-8 px-2 rounded-full text-xs"
                            >
                              🔗 Eşle
                            </Button>
                          )}
                          {g.mapped_to && (
                            <Button
                              data-testid={`wgroup-remove-${idx}`}
                              size="sm"
                              variant="outline"
                              onClick={() => removeMapping(g.wolvox_group)}
                              className="h-8 px-2 rounded-full text-xs text-red-600"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {grouped.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-6 text-[#1B2A22]/60">
                      Henüz Wolvox'tan grup bilgisi gelmemiş. İlk sync'ten sonra burada görünür.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
